import React from "react";
import { BedDouble, Hotel, Plus, Trash2, X } from "lucide-react";
import { PhotoStrip } from "./PhotoUploader";
import HotelPerksEditor from "./HotelPerksEditor";
import { confirmAsync } from "@/lib/safeConfirm";
import { uid } from "./utils";

// AccommodationsTab — the hotels of a curated trip, exactly as the client
// sees them on the landing page ("Where you stay"): name, city, dates,
// description, room labels and photos. Travefy brings hotels in with no
// photos and no room label (the card fell back to "1 × Room"), so this is
// where the agent finishes them off (2026-09-23).
const ROOM_LABELS = {
  doble: "Double room",
  individual: "Single room",
  triple: "Triple room",
  suite: "Suite",
};

const nightsBetween = (from, to) => {
  if (!from || !to) return 0;
  const ms = new Date(to) - new Date(from);
  return ms > 0 ? Math.round(ms / 86400000) : 0;
};

// What the public card will print for a room: the explicit label wins, then
// the Travefy name, then a readable version of the pricing room_type.
const roomLabel = (r) => r.type || r.name || ROOM_LABELS[r.room_type] || "";

export default function AccommodationsTab({ trip, onChange }) {
  const accs = trip.accommodations || [];

  const update = (accId, patch) => onChange({
    accommodations: accs.map((a) => (a.acc_id === accId ? { ...a, ...patch } : a)),
  });

  const add = () => {
    const last = accs[accs.length - 1];
    onChange({
      accommodations: [...accs, {
        acc_id: uid("acc"),
        name: "", city: last?.city || "", address: "",
        date_from: last?.date_to || trip.start_date || "",
        date_to: "", description: "",
        rooms: [{ room_id: uid("room"), type: "Double room", pax: 2 }],
        image_urls: [],
        price_tax_excl: 0, price_tax_incl: 0, price: 0,
        currency: trip.currency || "EUR",
      }],
    });
  };

  // confirmAsync, not window.confirm: the preview iframe drops modals and
  // a native confirm silently returns undefined (the button would do nothing).
  const remove = async (acc) => {
    const ok = await confirmAsync(
      `¿Quitar "${acc.name || "este hotel"}" de la ficha del cliente?`,
      { confirmLabel: "Quitar", destructive: true },
    );
    if (!ok) return;
    onChange({ accommodations: accs.filter((a) => a.acc_id !== acc.acc_id) });
  };

  return (
    <div data-testid="hotels-tab">
      <div className="flex items-center justify-between mb-6">
        <div className="text-sm text-clay-600">
          {accs.length} {accs.length === 1 ? "hotel" : "hoteles"} en la ficha del cliente
        </div>
        <button
          onClick={add}
          data-testid="add-hotel"
          className="inline-flex items-center gap-1.5 text-sm px-3 py-2 bg-clay-900 text-white rounded hover:bg-clay-800"
        >
          <Plus size={14}/> Añadir hotel
        </button>
      </div>

      {accs.length === 0 && (
        <div
          className="border border-dashed border-clay-300 rounded p-12 text-center"
          data-testid="hotels-empty"
        >
          <Hotel size={26} className="mx-auto text-clay-400"/>
          <div className="text-sm text-clay-600 mt-3">
            Este viaje todavía no tiene hoteles. Añade el primero y aparecerá en
            la sección «Where you stay» de la web del cliente.
          </div>
        </div>
      )}

      <HotelPerksEditor
        perks={trip.hotel_perks}
        onChange={(hotel_perks) => onChange({ hotel_perks })}
        testid="curated-hotel-perks"
      />

      <div className="space-y-5 mt-6">
        {accs.map((a, idx) => (
          <HotelCard
            key={a.acc_id}
            acc={a}
            idx={idx}
            onUpdate={(patch) => update(a.acc_id, patch)}
            onRemove={() => remove(a)}
          />
        ))}
      </div>
    </div>
  );
}

function HotelCard({ acc, idx, onUpdate, onRemove }) {
  const nights = nightsBetween(acc.date_from, acc.date_to);
  const rooms = acc.rooms || [];

  const updateRoom = (roomId, patch) => onUpdate({
    rooms: rooms.map((r) => (r.room_id === roomId ? { ...r, ...patch } : r)),
  });

  return (
    <div className="border border-clay-300 bg-white" data-testid={`hotel-${acc.acc_id}`}>
      <div className="flex items-center gap-3 px-5 py-3 border-b border-clay-200 bg-clay-50">
        <BedDouble size={15} className="text-clay-500 shrink-0"/>
        <span className="text-[11px] tracking-widest uppercase text-clay-500">
          Hotel {idx + 1}
        </span>
        {nights > 0 && (
          <span className="text-[11px] text-clay-500">
            · {nights} {nights === 1 ? "noche" : "noches"}
          </span>
        )}
        <button
          onClick={onRemove}
          title="Quitar hotel"
          data-testid={`hotel-remove-${acc.acc_id}`}
          className="ml-auto text-clay-400 hover:text-terracotta"
        >
          <Trash2 size={14}/>
        </button>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <Labelled label="Nombre del hotel">
            <input
              value={acc.name || ""}
              onChange={(e) => onUpdate({ name: e.target.value })}
              placeholder="Hotel Principe Di Villafranca"
              data-testid={`hotel-name-${acc.acc_id}`}
              className={inputCls}
            />
          </Labelled>
          <Labelled label="Ciudad">
            <input
              value={acc.city || ""}
              onChange={(e) => onUpdate({ city: e.target.value })}
              placeholder="Palermo"
              data-testid={`hotel-city-${acc.acc_id}`}
              className={inputCls}
            />
          </Labelled>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <Labelled label="Check-in">
            <input
              type="date" value={acc.date_from || ""}
              onChange={(e) => onUpdate({ date_from: e.target.value })}
              data-testid={`hotel-from-${acc.acc_id}`}
              className={inputCls}
            />
          </Labelled>
          <Labelled label="Check-out">
            <input
              type="date" value={acc.date_to || ""}
              onChange={(e) => onUpdate({ date_to: e.target.value })}
              data-testid={`hotel-to-${acc.acc_id}`}
              className={inputCls}
            />
          </Labelled>
          <Labelled label="Dirección (opcional)" span={2}>
            <input
              value={acc.address || ""}
              onChange={(e) => onUpdate({ address: e.target.value })}
              placeholder="Via G. Turrisi Colonna, 4"
              data-testid={`hotel-address-${acc.acc_id}`}
              className={inputCls}
            />
          </Labelled>
        </div>

        <Labelled label="Descripción que ve el cliente">
          <textarea
            value={acc.description || ""}
            onChange={(e) => onUpdate({ description: e.target.value })}
            rows={4}
            placeholder="Un boutique hotel de diseño en el corazón de Palermo…"
            data-testid={`hotel-description-${acc.acc_id}`}
            className={`${inputCls} resize-y leading-relaxed`}
          />
        </Labelled>

        <div>
          <div className="text-[11px] tracking-widest uppercase text-clay-500 mb-2">
            Habitaciones · «Your rooms»
          </div>
          <div className="space-y-2">
            {rooms.map((r) => (
              <div key={r.room_id} className="flex items-center gap-2" data-testid={`hotel-room-${r.room_id}`}>
                <input
                  value={roomLabel(r)}
                  onChange={(e) => updateRoom(r.room_id, { type: e.target.value })}
                  placeholder="Double room"
                  data-testid={`hotel-room-label-${r.room_id}`}
                  className={`${inputCls} max-w-xs`}
                />
                <input
                  type="number" min="0"
                  value={r.size_sqm ?? ""}
                  onChange={(e) => updateRoom(r.room_id, {
                    size_sqm: e.target.value === "" ? null : Number(e.target.value),
                  })}
                  placeholder="m²"
                  data-testid={`hotel-room-size-${r.room_id}`}
                  className={`${inputCls} w-24`}
                />
                <button
                  onClick={() => onUpdate({ rooms: rooms.filter((x) => x.room_id !== r.room_id) })}
                  title="Quitar habitación"
                  data-testid={`hotel-room-remove-${r.room_id}`}
                  className="text-clay-400 hover:text-terracotta"
                >
                  <X size={13}/>
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => onUpdate({
              rooms: [...rooms, { room_id: uid("room"), type: "Double room", pax: 2 }],
            })}
            data-testid={`hotel-room-add-${acc.acc_id}`}
            className="mt-2 text-[12px] text-clay-600 hover:text-terracotta inline-flex items-center gap-1"
          >
            <Plus size={12}/> Añadir habitación
          </button>
        </div>

        <div>
          <div className="text-[11px] tracking-widest uppercase text-clay-500 mb-2">
            Fotos del hotel
          </div>
          <PhotoStrip
            photos={acc.image_urls || []}
            onChange={(image_urls) => onUpdate({ image_urls })}
            testid={`hotel-photos-${acc.acc_id}`}
          />
          <div className="text-[11px] text-clay-400 mt-1.5">
            La web del cliente muestra hasta 7 fotos; sin fotos, la ficha usa el
            diseño editorial con la descripción como protagonista.
          </div>
        </div>
      </div>
    </div>
  );
}

const inputCls = "w-full border border-clay-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-terracotta";

function Labelled({ label, span, children }) {
  return (
    <div className={span === 2 ? "col-span-2" : ""}>
      <div className="text-[11px] tracking-widest uppercase text-clay-500 mb-1.5">{label}</div>
      {children}
    </div>
  );
}
